import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import supportApi, { CreateTicketData } from '../services/supportApi';
import { colors } from '../styles/theme';

interface SupportDialogProps {
  open: boolean;
  onClose: () => void;
}

const SupportDialog: React.FC<SupportDialogProps> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string>('');

  const [formData, setFormData] = useState<CreateTicketData>({
    title: '',
    description: '',
    category: '',
    priority: 'medium',
  });

  const categories = [
    { value: 'technical', label: 'Teknik Sorun' },
    { value: 'account', label: 'Hesap Sorunu' },
    { value: 'billing', label: 'Fatura/Ödeme' },
    { value: 'feature', label: 'Özellik İsteği' },
    { value: 'bug', label: 'Hata Bildirimi' },
    { value: 'other', label: 'Diğer' },
  ];

  const priorities = [
    { value: 'low', label: 'Düşük' },
    { value: 'medium', label: 'Orta' },
    { value: 'high', label: 'Yüksek' },
    { value: 'urgent', label: 'Acil' },
  ];

  const handleChange = (field: keyof CreateTicketData, value: string) => {
    setFormData({ ...formData, [field]: value });
    setError('');
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.title.trim()) {
      setError('Lütfen bir başlık girin');
      return;
    }
    if (!formData.description.trim()) {
      setError('Lütfen açıklama girin');
      return;
    }

    try {
      setLoading(true);
      setError('');

      await supportApi.createTicket(formData);

      setSuccess(true);
      setFormData({
        title: '',
        description: '',
        category: '',
        priority: 'medium',
      });

      // Close dialog after 2 seconds
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('Error creating support ticket:', err);
      setError(err.response?.data?.message || 'Destek talebi oluşturulurken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        title: '',
        description: '',
        category: '',
        priority: 'medium',
      });
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          fontFamily: '"SF Pro Text", -apple-system, BlinkMacSystemFont, sans-serif',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: colors.navy,
          color: colors.white,
          pb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontFamily: 'inherit' }}>
          Yardım & Destek Talebi
        </Typography>
        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{ color: colors.white }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Destek talebiniz başarıyla oluşturuldu! En kısa sürede size dönüş yapacağız.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            fullWidth
            label="Başlık *"
            placeholder="Sorununuzu kısaca özetleyin"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            disabled={loading || success}
            error={!formData.title && !!error}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'inherit',
              },
            }}
          />

          <TextField
            fullWidth
            select
            label="Kategori"
            value={formData.category}
            onChange={(e) => handleChange('category', e.target.value)}
            disabled={loading || success}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'inherit',
              },
            }}
          >
            <MenuItem value="">Seçiniz (Opsiyonel)</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            select
            label="Öncelik"
            value={formData.priority}
            onChange={(e) => handleChange('priority', e.target.value)}
            disabled={loading || success}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'inherit',
              },
            }}
          >
            {priorities.map((priority) => (
              <MenuItem key={priority.value} value={priority.value}>
                {priority.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            multiline
            rows={5}
            label="Açıklama *"
            placeholder="Sorununuzu detaylı olarak açıklayın..."
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            disabled={loading || success}
            error={!formData.description && !!error}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'inherit',
              },
            }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
            * Zorunlu alanlar
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            fontFamily: 'inherit',
            textTransform: 'none',
          }}
        >
          İptal
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || success}
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          sx={{
            fontFamily: 'inherit',
            textTransform: 'none',
            backgroundColor: colors.navy,
            '&:hover': {
              backgroundColor: colors.darkNavy,
            },
          }}
        >
          {loading ? 'Gönderiliyor...' : 'Talep Oluştur'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SupportDialog;
